import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeApi } from '../axios';

// ─── Issued Documents ─────────────────────────────────────────────────────────

export const useIssuedDocuments = (params = {}) => useQuery({
  queryKey: ['documents', 'issued', params],
  queryFn: () => employeeApi.get('/v1/documents/issued', { params }).then(r => r.data),
  staleTime: 30_000,
});

export const useIssueDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/documents/issued', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'issued'] }),
  });
};

export const useAcknowledgeDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => employeeApi.put(`/v1/documents/issued/${id}/acknowledge`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'issued'] }),
  });
};

// ─── Materials ────────────────────────────────────────────────────────────────

export const useMaterials = (params = {}) => useQuery({
  queryKey: ['documents', 'materials', params],
  queryFn: () => employeeApi.get('/v1/documents/materials', { params }).then(r => r.data),
  staleTime: 30_000,
});

export const useIssueMaterial = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/documents/materials', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'materials'] }),
  });
};

export const useUpdateMaterial = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => employeeApi.put(`/v1/documents/materials/${id}`, payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'materials'] }),
  });
};

// ─── Full & Final ─────────────────────────────────────────────────────────────

export const useFullFinalList = () => useQuery({
  queryKey: ['documents', 'full-final'],
  queryFn: () => employeeApi.get('/v1/documents/full-final').then(r => r.data),
  staleTime: 30_000,
});

export const useFullFinal = (empId) => useQuery({
  queryKey: ['documents', 'full-final', empId],
  queryFn: () => employeeApi.get(`/v1/documents/full-final/${empId}`).then(r => r.data),
  enabled: !!empId,
  staleTime: 15_000,
});

export const useInitiateFullFinal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => employeeApi.post('/v1/documents/full-final', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', 'full-final'] }),
  });
};

export const useUpdateFullFinal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ empId, ...payload }) => employeeApi.put(`/v1/documents/full-final/${empId}`, payload).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['documents', 'full-final', vars.empId] });
      qc.invalidateQueries({ queryKey: ['documents', 'full-final'] });
    },
  });
};

export const useCloseFullFinal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ empId, ...payload }) => employeeApi.post(`/v1/documents/full-final/${empId}/close`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};
